import { Cesium } from 'app/cesium';

interface CesiumLabelSpec {
    eyeOffset?: Cesium.Cartesian3;
    fillColor?: Cesium.Color;
    font?: string;
    horizontalOrigin?: Cesium.HorizontalOrigin;
    pixelOffset?: Cesium.Cartesian2;
    pixelOffsetScaleByDistance?: Cesium.NearFarScalar;
    position?: Cesium.PositionProperty;
    scaleByDistance?: Cesium.NearFarScalar;
    show?: boolean;
    style?: Cesium.LabelStyle;
    text?: string;
    translucencyByDistance?: Cesium.NearFarScalar;
    verticalOrigin?: Cesium.VerticalOrigin;
}

export class CesiumTransientLabel {
    private _label: Cesium.Label;
    private readonly _labelCollection: Cesium.LabelCollection;
    private readonly _labelSpec: CesiumLabelSpec;

    /**
     * Cesium labels are real slow! Each character is a billboard.
     * This class just wraps a cesium label and adds or removes it from the
     * label collection only if the label is shown. This is useful for when you have lots of labels that are rarely
     * shown but you want a simpler interface.
     * Careful this class does have a destroy method on it!
     */
    constructor(labelCollection: Cesium.LabelCollection, labelSpec: CesiumLabelSpec) {
        this._labelCollection = labelCollection;
        // some nasty stuff here so the initial value is honoured inside the setter
        const originalShow = labelSpec.show;
        this._labelSpec = labelSpec;
        this.show = false;
        this.show = originalShow;
    }

    set text(text: string) {
        this._labelSpec.text = text;
        if (this._label) {
            this._label.text = text;
        }
    }

    set position(position: Cesium.PositionProperty) {
        this._labelSpec.position = position;
        if (this._label) {
            this._label.position = position as unknown as Cesium.Cartesian3;
        }
    }

    set eyeOffset(eyeOffset: Cesium.Cartesian3) {
        this._labelSpec.eyeOffset = eyeOffset;
        if (this._label) {
            this._label.eyeOffset = eyeOffset;
        }
    }

    set pixelOffset(pixelOffset: Cesium.Cartesian2) {
        this._labelSpec.pixelOffset = pixelOffset;
        if (this._label) {
            this._label.pixelOffset = pixelOffset;
        }
    }

    set verticalOrigin(verticalOrigin: Cesium.VerticalOrigin) {
        this._labelSpec.verticalOrigin = verticalOrigin;
        if (this._label) {
            this._label.verticalOrigin = verticalOrigin;
        }
    }

    set horizontalOrigin(horizontalOrigin: Cesium.HorizontalOrigin) {
        this._labelSpec.horizontalOrigin = horizontalOrigin;
        if (this._label) {
            this._label.horizontalOrigin = horizontalOrigin;
        }
    }

    set show(show: boolean) {
        if (this._labelSpec.show === show) {
            return;
        }
        this._labelSpec.show = show;
        if (show) {
            this._label = this._labelCollection.add(this._labelSpec);
        } else if (this._label) {
            this._labelCollection.remove(this._label);
            this._label = null;
        }
    }

    destroy() {
        // destroy the label if need
        this.show = false;
    }
}
